import {$, $$} from "./Dom.ts";
import Dropzone from "dropzone";
import {$get, $postForm} from "./Ajax.ts";
import {createSuccessNotification} from "../../portail/js/Notifications.ts";
import {createFormData} from "./FormHelper.ts";
import {findCkeditorInstance, refreshPage} from "./Utils.ts";

let registered = false

// on rend accessible Dropzone au global pour pouvoir l'utiliser dans les tests selenium
if (!window.Dropzone) {
    window.Dropzone = Dropzone
}

export const initDropzone = (element: HTMLElement | null) => {
    if (element) {
        const dropzone = new Dropzone(element, {
            paramName: "fichier", // Nom de la donnée post envoyer
            parallelUploads: parseInt(element.getAttribute("data-max-file") ?? ""), // Autorise l'upload parallel
            addRemoveLinks: true, // Ajoute un lien pour retirer la pj
            maxFiles: parseInt(element.getAttribute("data-max-file") ?? ""), // Nombre de fichiers autorisé
            uploadMultiple: (element.getAttribute("data-upload_multiple") === 'true'), // Autorise le multi upload
            maxFilesize: parseInt(element.getAttribute("data-max-file-size") ?? ""), // Taille maximal d'un fichier
            autoProcessQueue: false, // Empêche l'envoi automatique
            thumbnailMethod: "contain", // Style des miniuature
            acceptedFiles: window.Openscop.VALID_EXTENSIONS, // Extensions autorisé
            dictFallbackMessage: "Votre navigateur ne supporte le glisser déposer de document",
            dictFileTooBig: "Le fichier est trop volumineux ({{filesize}}MiB). Taille de fichier maximum: {{maxFilesize}}MiB.",
            dictInvalidFileType: "Type de fichier non autorisé.",
            dictResponseError: "Le serveur a répondu avec une erreur {{statusCode}} code.",
            dictCancelUpload: "Annuler l'envoi",
            dictCancelUploadConfirmation: "Voulez vous vraiment annuler cette envoi ?",
            dictRemoveFile: "Supprimer le fichier",
            dictMaxFilesExceeded: "Vous ne pouvez pas ajouter de fichier supplémentaire. Nombre d'envoie de fichier autorisé : {{maxFiles}}",
        })
        dropzone.on("removedfile", (file) => handleRemoveFile(element, file))
        dropzone.on("success", () => handleSuccess(element))
        dropzone.on("sending", (_data: Dropzone.DropzoneFile, _xhr: XMLHttpRequest, formData: FormData) => handleSending(element, formData))

        // Envoie les fichiers
        // if (!registered) {
            initDropZoneListeners(dropzone)
            registered = true
        // }
    }
}

function handleRemoveFile(element: HTMLElement, file: Dropzone.DropzoneFile) {
    const url = element.getAttribute('action')
    if (url) {
        const form = new FormData()
        form.append('effacer', 'true')
        form.append('pj_name', file.name)
        $postForm(url, form)
    }
}

function handleSuccess(element: HTMLElement) {
    createSuccessNotification({
        message: 'Pièce jointe envoyé!'
    })
    // Une fois le fichier envoyer:
    // Si formulaire organisateur : recharge uniquement le panel des pj
    if (element.classList.contains('dropzone-pj-organisateur')) {
        $get(element.getAttribute('data-url-org-reload')!).then((data) => {
            element.dispatchEvent(new CustomEvent('dropzone-uploaded', {detail: data}))
        })
    }
    // Sinon recharge la page
    else {
        refreshPage()
    }
}

function handleSending(element: HTMLElement, formData: FormData) {
    // Dans le cadre d'un ajout de pièce jointe suplémentaire organisateur
    // On ajoute les valeurs au formulaire avant d'envoyer
    if (element.classList.contains('dropzone-pj-organisateur-supp')) {
        handleSendingOrganisateur(formData)
    }
    // Dans le cadre d'un ajout de précision de demande a un avis
    // On ajoute les valeurs au formulaire avant d'envoyer
    if (element.classList.contains('dropzone-pj-objet-demande')) {
        handleSendingPjDemande(element, formData)
    }
    // Dans le cadre d'un ajout de note
    // On ajoute les valeurs au formulaire avant d'envoyer
    if (element.classList.contains('dropzone-pj-note')) {
        handleSendingPjNote(element, formData)
    }
    // Dans le cadre d'une modification de note
    // On ajoute les valeurs au formulaire avant d'envoyer
    if (element.classList.contains('dropzone-pj-note-modif')) {
        handleSendingPjNoteModif(formData)
    }
}

function handleSendingPjDemande(element: HTMLElement, formData: FormData) {
    const demande = $<HTMLInputElement>(`#objet_demande_text_${element.id.split("_")[2]}`)?.value
    if (demande) formData.append("objet_demande_text", demande);
}

function handleSendingOrganisateur(formData: FormData) {
    const portee = $<HTMLInputElement>("#portee")?.value
    const nom = $<HTMLInputElement>("#name_pj")?.value
    const aide = $<HTMLInputElement>("#aide_supp")?.value
    if (nom && aide) {
        if (portee) formData.append("portee", portee);
        formData.append("nom", nom);
        formData.append("aide", aide);
    }
}

function handleSendingPjNote(element: HTMLElement, formData: FormData) {
    const id = element.id.split("_")[3]
    const note = findCkeditorInstance(`note_text_${id}`).getData()
    const instructeur = $<HTMLInputElement>(`#note_lisible_tous_instructeurs_${id}`)?.checked
    const services = $<HTMLInputElement>(`#note_lisible_services_consultes_${id}`)?.checked
    if (note) {
        formData.append("note_text", note);
        if (instructeur) formData.append("lisible_tous_instructeurs", String(instructeur));
        if (services) formData.append("lisible_services_consultes", String(services));
    }
}

function handleSendingPjNoteModif(formData: FormData) {

    const note = findCkeditorInstance("note_text").getData()
    const instructeurs = !!$<HTMLInputElement>("#note_lisible_tous_instructeurs")?.checked
    const services = !!$<HTMLInputElement>("#note_lisible_services_consultes")?.checked
    const archive = !!$<HTMLInputElement>("#note_archivee")?.checked
    formData.append("note_text", note)
    formData.append("lisible_tous_instructeurs", String(instructeurs));
    formData.append("lisible_services_consultes", String(services));
    formData.append("archivee", String(archive));
}

function initDropZoneListeners(dropzone: Dropzone) {
    document.body.addEventListener('click', handleDropzoneListeners)
    document.body.addEventListener('keyup', handleDropzoneListeners)
    $$('.dz-message', dropzone.element).forEach(element => {
        element.addEventListener('keyup', (event) => {
            if ((event.type === "keyup" && (event.code === "Enter" || event.code === "NumpadEnter"))) {
                dropzone.hiddenFileInput?.click()
            }
        })
    })
}

function handleDropzoneListeners(e: DOMEvent<HTMLElement>) {
    if ((e.type === "keyup" && (e.code === "Enter" || e.code === "NumpadEnter")) || e.type === "click") {
        const target = e.target
        if (target.classList.contains('btn_submit_dropzone_pj')) {
            e.preventDefault();
            handleDropzonePj(target)
        } else if (target.classList.contains('btn_submit_dropzone_note')) {
            e.preventDefault();
            handleDropzoneNote(target)
        } else if (target.classList.contains('btn_submit_dropzone_note_modif')) {
            e.preventDefault();
            handleDropzoneNoteModif(target)
        }
    }
}

function handleDropzonePj(target: HTMLElement) {
    const id = target.id.split("_")[1];
    const myDropzone = Dropzone.forElement(`#dropzone_form_${id}`);
    if (target.classList.contains('multiple_processing')) {
        // Dans le cadre d'ajout d'objet de la demande a un avis sans pièce jointe
        // On envoie juste l'objet de la demande
        const objet_demande_text = $<HTMLInputElement>("#objet_demande_text_" + id)?.value
        if (target.classList.contains('btnsubmit_objet_demande') && myDropzone.files.length === 0 && objet_demande_text) {
            const url = myDropzone.element.getAttribute('action')
            const data = createFormData({
                objet_demande_text,
            })
            if (url) {
                $postForm(url, data).then(() => {
                    location.reload()
                })
            }
        }
        // Sinon on envoie les fichiers
        myDropzone.processQueue();
        myDropzone.getQueuedFiles().forEach(() => myDropzone.processQueue())
    } else {
        myDropzone.processQueue();
    }
}

function handleDropzoneNote(target: HTMLElement) {
    const id = target.id.split("_")[2];
    const myDropzone = Dropzone.forElement(`#dropzone_form_note_${id}`);
    // Dans le cadre d'ajout de note sans pièce jointe
    // On envoie juste la note
    // const note_text = findCkeditorInstance(`note_text_${id}`).getData()
    const note_text = findCkeditorInstance(`note_text_${id}`).getData()
    const lisible_tous_instructeurs = $<HTMLInputElement>("#note_lisible_tous_instructeurs_" + id)?.checked
    const lisible_services_consultes = $<HTMLInputElement>("#note_lisible_services_consultes_" + id)?.checked
    if (target.classList.contains('btnsubmit_note') && myDropzone.files.length === 0 && note_text) {
        const url = myDropzone.element.getAttribute('action')
        const data = createFormData({
            note_text,
            lisible_tous_instructeurs,
            lisible_services_consultes,
        })
        if (url) {
            $postForm(url, data).then(() => {
                location.reload()
            })
        }
    } else {
        myDropzone.processQueue();
    }
}

function handleDropzoneNoteModif(target: HTMLElement) {
    const myDropzone = Dropzone.forElement("#dropzone_form_note");
    // Dans le cadre d'ajout de note sans pièce jointe
    // On envoie juste la note
    const note_text = findCkeditorInstance(`note_text`).getData()
    const lisible_tous_instructeurs = $<HTMLInputElement>("#note_lisible_tous_instructeurs")?.checked
    const lisible_services_consultes = $<HTMLInputElement>("#note_lisible_services_consultes")?.checked
    const archivee = $<HTMLInputElement>("#note_archivee")?.checked
    if (target.classList.contains('btnsubmit_note_modif') && myDropzone.files.length === 0 && note_text) {
        const url = myDropzone.element.getAttribute('action')
        if (url) {
            const data = createFormData({
                note_text,
                lisible_tous_instructeurs,
                lisible_services_consultes,
                archivee,
            })
            $postForm(url, data).then(() => {
                location.reload()
            })
        }
    } else {
        myDropzone.processQueue();
    }
}